import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, ShieldCheck, ShieldSlash, ListChecks, FloppyDisk,
  Plus, Trash, FlowArrow, ArrowsClockwise, Question,
} from "@phosphor-icons/react";

const TABS = [
  { key: "rules", label: "Rules" },
  { key: "decisions", label: "Decisions log" },
  { key: "test", label: "Test bench" },
];

export default function Gatekeeper() {
  const [tab, setTab] = useState("rules");

  return (
    <div className="space-y-8" data-testid="gatekeeper-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// governance · gatekeeper</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          Gatekeeper
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          The intermediary between customers and the knowledge base. Decides every turn:
          <span className="font-mono text-emerald-700 mx-1.5">grant</span>,
          <span className="font-mono text-rose-700 mx-1.5">deny</span>, or
          <span className="font-mono text-amber-700 mx-1.5">clarify</span> — using your
          rules first, with an LLM judge as a safety net.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="gatekeeper-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm transition-all relative ${tab === t.key ? "text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`gk-tab-${t.key}`}
          >
            {t.label}
            {tab === t.key && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-orange-600" />}
          </button>
        ))}
      </div>

      {tab === "rules" && <RulesEditor />}
      {tab === "decisions" && <DecisionsLog />}
      {tab === "test" && <TestBench />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Rules editor
// ────────────────────────────────────────────────────────────────────────

function RulesEditor() {
  const [meta, setMeta] = useState([]);
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/gatekeeper/rules");
      setMeta(data.submodules_meta || []);
      setRules(data.rules);
    } catch {
      toast.error("Couldn't load rules");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/gatekeeper/rules", {
        submodules: rules.submodules,
        custom_rules: rules.custom_rules,
      });
      setRules(data.rules);
      toast.success("Rules saved");
    } catch {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const updateSubmodule = (key, patch) => {
    setRules((r) => ({
      ...r,
      submodules: { ...r.submodules, [key]: { ...(r.submodules[key] || {}), ...patch } },
    }));
  };

  const addCustom = () => {
    setRules((r) => ({
      ...r,
      custom_rules: [
        ...(r.custom_rules || []),
        { id: crypto.randomUUID(), title: "", instruction: "", enabled: true },
      ],
    }));
  };
  const updateCustom = (id, patch) => {
    setRules((r) => ({
      ...r,
      custom_rules: (r.custom_rules || []).map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));
  };
  const removeCustom = (id) => {
    if (!window.confirm("Remove this rule?")) return;
    setRules((r) => ({ ...r, custom_rules: (r.custom_rules || []).filter((c) => c.id !== id) }));
  };

  if (!rules) {
    return <div className="p-12 text-center mono-label pulse-soft">Loading rules…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gk-submodules">
        {meta.map((m) => {
          const v = rules.submodules[m.key] || { enabled: false, instruction: "" };
          return (
            <div key={m.key} className="border border-slate-200 bg-white" data-testid={`gk-submodule-${m.key}`}>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield size={14} className={v.enabled ? "text-orange-600" : "text-slate-400"} />
                  <div>
                    <div className="text-sm font-medium text-slate-900">{m.title}</div>
                    <div className="mono-label">{m.description}</div>
                  </div>
                </div>
                <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={v.enabled}
                    onChange={(e) => updateSubmodule(m.key, { enabled: e.target.checked })}
                    className="accent-orange-600"
                    data-testid={`gk-toggle-${m.key}`}
                  />
                  {v.enabled ? "active" : "off"}
                </label>
              </div>
              <textarea
                rows={5}
                value={v.instruction}
                onChange={(e) => updateSubmodule(m.key, { instruction: e.target.value })}
                placeholder={`Write the ${m.title.toLowerCase()} instruction the gatekeeper must follow…`}
                className="w-full px-5 py-3 text-sm font-sans bg-white outline-none resize-none"
                data-testid={`gk-instruction-${m.key}`}
              />
            </div>
          );
        })}
      </div>

      <div className="border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks size={14} className="text-orange-600" />
            <div>
              <div className="text-sm font-medium text-slate-900">Custom rules</div>
              <div className="mono-label">Brand-specific overrides — applied alongside the five fixed submodules.</div>
            </div>
          </div>
          <button onClick={addCustom} className="btn-sharp btn-secondary text-sm" data-testid="gk-add-custom">
            <Plus size={14} /> Add rule
          </button>
        </div>
        <div className="divide-y divide-slate-100" data-testid="gk-custom-list">
          {(rules.custom_rules || []).length === 0 && (
            <div className="p-8 text-center text-sm italic text-slate-400">No custom rules yet.</div>
          )}
          {(rules.custom_rules || []).map((c) => (
            <div key={c.id} className="px-5 py-4" data-testid={`gk-custom-${c.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={c.title}
                  onChange={(e) => updateCustom(c.id, { title: e.target.value })}
                  placeholder="Rule title (e.g. Deflect non-ITC brand requests)"
                  className="flex-1 text-sm font-medium text-slate-900 outline-none border-b border-transparent focus:border-orange-200 px-0 py-1 bg-transparent"
                />
                <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={c.enabled}
                    onChange={(e) => updateCustom(c.id, { enabled: e.target.checked })}
                    className="accent-orange-600"
                  />
                  {c.enabled ? "active" : "off"}
                </label>
                <button onClick={() => removeCustom(c.id)} className="text-slate-400 hover:text-rose-600" title="Remove">
                  <Trash size={14} />
                </button>
              </div>
              <textarea
                rows={3}
                value={c.instruction}
                onChange={(e) => updateCustom(c.id, { instruction: e.target.value })}
                placeholder="What should the gatekeeper do when this rule applies?"
                className="w-full text-sm font-sans bg-white outline-none resize-none border border-slate-200 px-3 py-2"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 sticky bottom-2">
        <button onClick={load} className="btn-sharp btn-ghost text-sm" data-testid="gk-revert">
          <ArrowsClockwise size={14} /> Revert
        </button>
        <button onClick={save} disabled={saving} className="btn-sharp btn-primary text-sm" data-testid="gk-save">
          <FloppyDisk size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Decisions log
// ────────────────────────────────────────────────────────────────────────

const DECISION_FILTERS = [
  { key: "all", label: "All", color: "text-slate-700" },
  { key: "grant", label: "Granted", color: "text-emerald-700" },
  { key: "deny", label: "Denied", color: "text-rose-700" },
  { key: "clarify", label: "Clarified", color: "text-amber-700" },
];

function DecisionsLog() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? "" : `?decision=${filter}`;
      const { data } = await api.get(`/admin/gatekeeper/decisions${params}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Pill label="Total" value={totals.all || 0} />
        <Pill label="Granted" value={totals.grant || 0} tone="emerald" icon={ShieldCheck} />
        <Pill label="Denied" value={totals.deny || 0} tone="rose" icon={ShieldSlash} />
        <Pill label="Clarified" value={totals.clarify || 0} tone="amber" icon={Question} />
      </div>

      <div className="flex items-center gap-2">
        {DECISION_FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`btn-sharp text-xs ${filter === f.key ? "btn-primary" : "btn-secondary"}`}
            data-testid={`gk-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <button onClick={load} className="btn-sharp btn-ghost text-xs ml-auto" data-testid="gk-decisions-refresh">
          <ArrowsClockwise size={12} />
        </button>
      </div>

      <div className="border border-slate-200 bg-white" data-testid="gk-decisions-list">
        {loading ? (
          <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sm italic text-slate-400">No decisions logged yet.</div>
        ) : (
          rows.map((r, i) => (
            <div key={r.id} className="px-5 py-3 border-b border-slate-100 last:border-0" data-testid={`gk-row-${i}`}>
              <div className="flex items-start gap-3">
                <DecisionBadge decision={r.decision} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 truncate">{r.query}</div>
                  <div className="mono-label mt-0.5 flex flex-wrap gap-x-3">
                    <span>{r.user_email || "—"}</span>
                    <span>· stage: <span className="font-mono">{r.stage}</span></span>
                    <span>· reason: <span className="font-mono">{r.reason}</span></span>
                    <span>· {r.latency_ms}ms</span>
                    <span>· {fmt(r.created_at)}</span>
                  </div>
                  {r.user_message && (
                    <div className="text-xs text-slate-600 italic mt-1.5 line-clamp-2">"{r.user_message}"</div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Test bench
// ────────────────────────────────────────────────────────────────────────

function TestBench() {
  const [q, setQ] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const samples = useMemo(() => [
    "Tell me about Aashirvaad Multigrain Atta",
    "Tell me about Patanjali Atta",
    "What is my credit card number on file?",
    "Compare Aashirvaad with Pillsbury",
    "Where can I buy Sunfeast Dark Fantasy?",
  ], []);

  const run = async (text) => {
    const query = (text ?? q).trim();
    if (!query) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gatekeeper/test", { query });
      setHistory((h) => [{ query, ...data, ts: Date.now() }, ...h].slice(0, 20));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="gk-testbench">
      <div className="border border-slate-200 bg-white p-5">
        <div className="mono-label mb-3 flex items-center gap-2">
          <FlowArrow size={12} className="text-orange-600" />
          Dry-run a query against the live rules
        </div>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="e.g. Tell me about Patanjali Atta"
            className="flex-1 border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-300"
            data-testid="gk-test-input"
          />
          <button onClick={() => run()} disabled={busy} className="btn-sharp btn-primary text-sm" data-testid="gk-test-run">
            {busy ? "…" : "Evaluate"}
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {samples.map((s) => (
            <button
              key={s}
              onClick={() => { setQ(s); run(s); }}
              className="text-xs px-2 py-1 border border-slate-200 bg-slate-50 hover:bg-orange-50 hover:border-orange-200 transition-colors"
              data-testid={`gk-sample-${s.slice(0, 12)}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2" data-testid="gk-test-history">
        {history.length === 0 && (
          <div className="text-sm italic text-slate-400 text-center py-6">Run a query to see how the gatekeeper would handle it.</div>
        )}
        {history.map((h, i) => (
          <div key={h.ts} className="border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <DecisionBadge decision={h.decision} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-900">{h.query}</div>
                <div className="mono-label mt-1">
                  stage <span className="font-mono">{h.stage}</span>
                  · reason <span className="font-mono">{h.reason}</span>
                  · {h.latency_ms}ms
                </div>
                {h.user_message && (
                  <div className="text-sm text-slate-700 italic mt-2 border-l-2 border-orange-200 pl-3">"{h.user_message}"</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Atoms
// ────────────────────────────────────────────────────────────────────────

function Pill({ label, value, tone = "slate", icon: Icon }) {
  const colors = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    amber: "text-amber-700",
  };
  return (
    <div className="border border-slate-200 bg-white p-4">
      <div className="mono-label flex items-center gap-1.5">
        {Icon && <Icon size={12} className={colors[tone]} />}
        {label}
      </div>
      <div className={`font-display text-2xl mt-1 ${colors[tone] || colors.slate}`}>{value}</div>
    </div>
  );
}

function DecisionBadge({ decision }) {
  if (decision === "grant") {
    return (
      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0 mt-0.5">
        GRANT
      </span>
    );
  }
  if (decision === "deny") {
    return (
      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 shrink-0 mt-0.5">
        DENY
      </span>
    );
  }
  return (
    <span className="text-[10px] font-mono px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 shrink-0 mt-0.5">
      CLARIFY
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}
